import { REPORT_GROUPS } from './reportCodes';

export const TEMPLATE_STATUSES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
};

export const SA_VALUES = [0, 1, 2, 3];
export const DA_VALUES = [1, 2, 3];
export const HS_VALUES = [0, 1, 2];
export const D_VALUES = [0, 1, 2, 3, 4];
export const CS_VALUES = [0, 1, 2, 3, 4, 5];

/**
 * Template edit/publish is Eric-class / template-owner work.
 * vasi-admin currently only enforces the Firebase `admin` custom claim
 * (see src/index.js). Until a narrower role exists:
 *   - `templateOwner: true` is the explicit Eric-class grant
 *   - `admin: true` is also allowed (every signed-in admin)
 * Set REACT_APP_TEMPLATE_OWNER_ONLY=true to require templateOwner only.
 */
export function canEditInterpretationTemplates(claims) {
  if (!claims) return false;
  if (claims.templateOwner === true) return true;
  if (process.env.REACT_APP_TEMPLATE_OWNER_ONLY === 'true') return false;
  return claims.admin === true;
}

export function makeGroupKey({ group, sa, da, hs, d } = {}) {
  if (group == null || sa == null || da == null || hs == null) return null;
  let key = `${group}; SA=${sa}; DA=${da}; HS=${hs}`;
  if (d !== null && d !== undefined && (group === 'FC' || group === 'MC')) {
    key += `; D=${d}`;
  }
  return key;
}

export function makeCautionKey(cs) {
  if (cs === null || cs === undefined) return null;
  return `CS=${cs}`;
}

export function normalizeTemplateKey(key) {
  return String(key ?? '').replace(/\s+/g, '').toUpperCase();
}

export function lookupProse(keys, key) {
  if (!keys || !key) return '';
  if (typeof keys[key] === 'string') return keys[key];
  const wanted = normalizeTemplateKey(key);
  const match = Object.entries(keys).find(([candidate]) => normalizeTemplateKey(candidate) === wanted);
  return match ? match[1] : '';
}

export function evaluationTemplateKeys(codes) {
  const needed = [];
  const cautionKey = makeCautionKey(codes?.caution?.cs);
  if (cautionKey) needed.push(cautionKey);

  REPORT_GROUPS.forEach((group) => {
    const scored = codes?.groups?.[group.id];
    const key = makeGroupKey({
      group: group.id,
      sa: scored?.sa,
      da: scored?.da,
      hs: scored?.hs,
      d: group.hasD ? scored?.d : null,
    });
    if (key) needed.push(key);
  });

  return needed;
}

export function allPermutationKeys(groupId) {
  const group = REPORT_GROUPS.find((entry) => entry.id === groupId);
  if (!group) return [];
  const keys = [];
  SA_VALUES.forEach((sa) => {
    DA_VALUES.forEach((da) => {
      HS_VALUES.forEach((hs) => {
        if (group.hasD) {
          D_VALUES.forEach((d) => {
            keys.push(makeGroupKey({ group: groupId, sa, da, hs, d }));
          });
        } else {
          keys.push(makeGroupKey({ group: groupId, sa, da, hs }));
        }
      });
    });
  });
  return keys;
}

export function catalogTemplateKeys() {
  return [
    ...CS_VALUES.map((cs) => makeCautionKey(cs)),
    ...REPORT_GROUPS.flatMap((group) => allPermutationKeys(group.id)),
  ];
}

export function missingTemplateKeys(keys, needed) {
  return (needed ?? []).filter((key) => !lookupProse(keys, key));
}

const GROUP_FIELDS = ['SA', 'DA', 'HS'];
const CHILD_GROUP_FIELDS = ['SA', 'DA', 'HS', 'D'];

function codeFieldNames(parsed) {
  if (parsed.kind === 'caution') return ['CS'];
  return parsed.d === null ? GROUP_FIELDS : CHILD_GROUP_FIELDS;
}

/**
 * Parse an Eric-style key back into codes. Tolerates spacing/case like
 * lookupProse does. Strict about shape: group keys need exactly SA, DA, HS,
 * plus D for child (hasD) groups and never D otherwise; CS keys are CS alone.
 * Returns null for anything else.
 */
export function parseTemplateKey(key) {
  const parts = normalizeTemplateKey(key).split(';').filter(Boolean);
  if (parts.length === 0) return null;
  const fields = {};
  let group = null;
  for (const part of parts) {
    const match = part.match(/^([A-Z]+)=(\d+)$/);
    if (match) {
      if (fields[match[1]] !== undefined) return null;
      fields[match[1]] = Number(match[2]);
    } else if (group === null && /^[A-Z]+$/.test(part)) {
      group = part;
    } else {
      return null;
    }
  }
  const names = Object.keys(fields);
  if (group === null) {
    if (names.length === 1 && names[0] === 'CS') return { kind: 'caution', cs: fields.CS };
    return null;
  }
  const entry = REPORT_GROUPS.find((candidate) => candidate.id === group);
  if (!entry) return null;
  const allowed = entry.hasD ? CHILD_GROUP_FIELDS : GROUP_FIELDS;
  if (names.length !== allowed.length || !allowed.every((name) => fields[name] !== undefined)) {
    return null;
  }
  return {
    kind: 'group',
    group,
    sa: fields.SA,
    da: fields.DA,
    hs: fields.HS,
    d: entry.hasD ? fields.D : null,
  };
}

function codeValues(parsed) {
  if (parsed.kind === 'caution') return { CS: parsed.cs };
  return { SA: parsed.sa, DA: parsed.da, HS: parsed.hs, D: parsed.d };
}

function canonicalKey(parsed) {
  return parsed.kind === 'caution' ? makeCautionKey(parsed.cs) : makeGroupKey(parsed);
}

/**
 * Existing keys with prose, ranked by closeness to targetKey, for
 * "Duplicate from…". Same group (or CS-to-CS) only. Order: fewest differing
 * code fields, then smallest total numeric distance, then catalog order
 * (lower code values first) so the result never depends on object order.
 * Keys that parse to the same codes as the target (e.g. SA=01) are excluded,
 * and codes are de-duplicated, so every result differs in at least one field.
 * Returns [{ key, prose, differing: ['SA', ...], distance }].
 */
export function nearestTemplateKeys(keys, targetKey, { limit = 5 } = {}) {
  const target = parseTemplateKey(targetKey);
  if (!target || !keys) return [];
  const fieldNames = codeFieldNames(target);
  const wanted = codeValues(target);
  const byCodes = new Map();

  Object.entries(keys).forEach(([key, prose]) => {
    if (typeof prose !== 'string' || !prose.trim()) return;
    const parsed = parseTemplateKey(key);
    if (!parsed || parsed.kind !== target.kind) return;
    if (parsed.kind === 'group' && parsed.group !== target.group) return;

    const values = codeValues(parsed);
    const differing = fieldNames.filter((name) => values[name] !== wanted[name]);
    if (differing.length === 0) return;
    const canonical = canonicalKey(parsed);
    const existing = byCodes.get(canonical);
    // Same codes spelled two ways: prefer the canonical spelling, then the
    // lexically first key, so the pick never depends on object order.
    if (existing) {
      const existingCanonical = existing.key === canonical;
      if (existingCanonical || (key !== canonical && existing.key < key)) return;
    }
    const delta = differing.reduce((sum, name) => sum + Math.abs(values[name] - wanted[name]), 0);
    byCodes.set(canonical, { key, prose, differing, distance: differing.length, delta, values });
  });

  const ranked = Array.from(byCodes.values());
  ranked.sort((left, right) => {
    if (left.distance !== right.distance) return left.distance - right.distance;
    if (left.delta !== right.delta) return left.delta - right.delta;
    for (const name of fieldNames) {
      if (left.values[name] !== right.values[name]) return left.values[name] - right.values[name];
    }
    return 0;
  });

  return ranked.slice(0, limit).map(({ key, prose, differing, distance }) => ({
    key, prose, differing, distance,
  }));
}

/**
 * Step through missing keys in catalog order, wrapping. `current` may be a
 * key that is no longer missing (just filled); stepping continues from its
 * catalog position. Returns null when nothing is missing.
 */
export function stepMissingKey({ ordered, missing, current, direction = 1 } = {}) {
  const list = ordered ?? [];
  const missingSet = new Set((missing ?? []).map(normalizeTemplateKey));
  const candidates = list.filter((key) => missingSet.has(normalizeTemplateKey(key)));
  if (candidates.length === 0) return null;
  const step = direction < 0 ? -1 : 1;
  const start = current == null
    ? -1
    : list.findIndex((key) => normalizeTemplateKey(key) === normalizeTemplateKey(current));
  if (start === -1) return step > 0 ? candidates[0] : candidates[candidates.length - 1];
  for (let offset = 1; offset <= list.length; offset += 1) {
    const index = (start + step * offset + list.length) % list.length;
    if (missingSet.has(normalizeTemplateKey(list[index]))) return list[index];
  }
  return null;
}

export function fillNameSlot(prose, clientName) {
  if (!prose) return '';
  const name = (clientName && String(clientName).trim()) || 'The client';
  return String(prose).replace(/\[\]/g, name);
}

export function assembleReport({ codes, templateKeys, clientName, sex, templateVersion, caseNumber } = {}) {
  const needed = evaluationTemplateKeys(codes);
  const sections = needed.map((key) => {
    const prose = lookupProse(templateKeys, key);
    return {
      key,
      missing: !prose,
      text: prose ? fillNameSlot(prose, clientName) : '',
    };
  });

  return {
    clientName: clientName || '',
    sex: sex || codes?.sex || null,
    caseNumber: caseNumber || null,
    templateVersion: templateVersion ?? null,
    codes,
    sections,
    missingKeys: sections.filter((section) => section.missing).map((section) => section.key),
    codesOnly: sections.every((section) => section.missing),
  };
}

export function draftDocId(sex) {
  return `${sex}_draft`;
}

export function publishedDocId(sex) {
  return `${sex}_published`;
}

export function versionedDocId(sex, version) {
  return `${sex}_v${version}`;
}
